import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const PartnerAuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

export function PartnerAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('partner_token') || '');
  const [partner, setPartner] = useState(() => {
    const raw = localStorage.getItem('partner_profile');
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  const isAuthed = !!token;

  useEffect(() => {
    if (token) localStorage.setItem('partner_token', token);
    else localStorage.removeItem('partner_token');
  }, [token]);

  async function validatePartnerKey(key) {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/partners/validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key })
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setToken(key);
        if (data && data.partner) {
          setPartner(data.partner);
          localStorage.setItem('partner_profile', JSON.stringify(data.partner));
        }
        return { ok: true };
      }
      // Backend rejected: return error (no demo fallback)
      return { ok: false, status: res.status };
    } catch (e) {
      // Network error: return proper error (no demo auth)
      return { ok: false, error: String(e) };
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    setToken('');
    setPartner(null);
    localStorage.removeItem('partner_profile');
  }

  const value = useMemo(() => ({ token, isAuthed, partner, loading, validatePartnerKey, logout }), [token, isAuthed, partner, loading]);
  return <PartnerAuthContext.Provider value={value}>{children}</PartnerAuthContext.Provider>;
}

export function usePartnerAuth() {
  const ctx = useContext(PartnerAuthContext);
  if (!ctx) throw new Error('usePartnerAuth must be used within PartnerAuthProvider');
  return ctx;
}